import get from 'lodash.get';
import merge from 'lodash.merge';
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router';
import Chart from '../';
import { base, colors } from './config';
import { transform } from './transformers';
import enIcon from '../../../../../images/language-code-icons/en.png';
import esIcon from '../../../../../images/language-code-icons/es.png';
import { tooltipTypes, Tooltip } from '../../../../Global/Tooltip';
import pendingImg from "../../../../../images/pending.png"
import { pendingStatuses } from '../../../../../services/displayStatus';
import { FEATURE_FLAGS } from '../../../../../constants/featureFlags';
import PrintButton from './PrintButton';
import PrintModal from './PrintModal';
import ParentLetterPrintWrapper, { printParentLetters } from './ParentLetterPrintWrapper';
import getParentLetterData from './getParentLetterData';
import { getUserData } from '../../../../../services/API';
import isAnyStudentAtRisk from './isAnyStudentAtRisk';
import PrintStatusModal, {PRINT_STATUS} from '../../Modal/PrintStatusModal';

const languageIcons = {
  'ES': esIcon,
  'EN': enIcon,
}

const RowDecorator = ({ name, score, locale, labeledScore, isPending }) => {
  return (
    <div className="dyslexiarisk-chart-row">
      {isPending ? <img className='dyslexia-pending-img' src={pendingImg}/> : <div className='dyslexia-pending-hide'/>}
      <div className="dyslexiarisk-chart-row-student-name">{name}</div>
      <div className="dyslexiarisk-chart-row-locale">
        {(score >= 0 || labeledScore)  && <img className="language-icon" src={languageIcons[locale]} alt=""/>}
      </div>
      {labeledScore && <div className="dyslexiarisk-chart-row-labeled-score">{labeledScore}</div>}
    </div>
  )
}

RowDecorator.propTypes = {
  name: PropTypes.string,
  score: PropTypes.string,
  locale: PropTypes.string,
}

const DyslexiaRisk = ({ report, onScroll, containerWidth }) => {

  const { dyslexiaParentLetterEnabled } = getUserData();

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [parentLetterData, setParentLetterData] = useState(null);
  const [includeActivityLog, setIncludeActivityLog] = useState(false);
  const [printLocale, setPrintLocale] = useState("en-us");
  const [printStatus, setPrintStatus] = useState(PRINT_STATUS.IDLE);

  const onChartLoad = (chart) => {
    // make updates once chart has loaded
  }

  const { scores, names, locales, labeledScores, } = transform([...report.scores.values()]);
  const cutoffs = report.cutoffs;
  const cutlines = report.cutlines;

  // estimate the position of cutlines to determine which to include
  const maxScore = scores[0];
  let cutlineSizerPositions = [];
  for(let i = 0; i < cutlines.length; i++) {
    cutlineSizerPositions[i] = false; // "false" position if this is an invalid cutline
    let value = cutlines[i].value;
    if(value > 0 && value <= maxScore) {
      cutlineSizerPositions[i] = ((containerWidth - 282) * (value / maxScore) * .857482185);
    }
  }

  // TODO refine this by moving prevprev tag by -4 and tag by 4, instead of just moving tag by 8

  // based on a proximity cutoff, generate an array of included cutlines
  let cutlineProximityCutoff = 20;
  let cutlineElements = [];
  for(let i = 0; i < cutlines.length; i++) {
    let position = cutlineSizerPositions[i];
    if(position) { // if this cutline hasn't been invalidated, consider including it
      let scaledPositionStyle = {left: 'calc(' + cutlines[i].value / maxScore*100 + '% * .857482185)'};

      // determine whether to include the cutline based on other cutline positions
      let includeCutline = true;
      let prevprevPosition = cutlineSizerPositions[(i - 2)];
      let prevPosition = cutlineSizerPositions[(i - 1)];
      if(i > 0) {
        if(prevPosition) {
          if(position - prevPosition < cutlineProximityCutoff) {
            includeCutline = false;
            cutlineSizerPositions[i] = false; // don't get rid of multiple adjacent cutlines
          }
        } else if (i > 1 && prevprevPosition) { // if three lines are close, move one of them
          if(position - prevprevPosition < cutlineProximityCutoff) {
            scaledPositionStyle = {left: 'calc(' + cutlines[i].value / maxScore*100 + '% * .857482185 + 8px)'};
            cutlineSizerPositions[i] = cutlineSizerPositions[i] + 8; // don't get rid of multiple adjacent cutlines
          }
        }
      }
      if(includeCutline) {
        cutlineElements.push({
          JSX: <span key={cutlines[i].label} className="dyslexiarisk-chart-label" style={scaledPositionStyle}>{cutlines[i].label}</span>,
          value: cutlines[i].value,
        });
      }
    }
  }

  let zones = [
    {value: cutoffs.MEDIUM_RISK, color: colors.GREAT},
    {value: cutoffs.HIGH_RISK, color: colors.AVERAGE},
    {color: colors.POOR},
  ];

  // use feature flag to toggle the visibility of the parent letters button
  // show print letters button if any students are at risk for dyslexia
  const isPrintButtonVisible = isAnyStudentAtRisk(report, cutoffs) && FEATURE_FLAGS.DYSLEXIA_PARENT_LETTERS && dyslexiaParentLetterEnabled;

  const yAxis = {
    yAxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      tickInterval: 25,
      gridLineWidth: 2,
      plotLines: cutlineElements.map((line) => ({
        color: 'black',
        width: 3,
        value: line.value,
        dashStyle: 'longdash',
        zIndex: 300
      })),
    }
  }

  const xAxis = {
    xAxis: {
      categories: names,
      tickWidth: 0,
      lineWidth: 0,
    }
  }

  // calculate height of the Dyslexia chart
  const pointWidth = 35;
  const margin = 10;
  const pointPadding = 0.4;
  const chartHeight = margin*2 + ((pointWidth * scores.length) * (1 + pointPadding));

  const styles = {
    chart: {
      height: chartHeight,
      margin: [margin, margin, margin, margin]
    }
  }

  const series = {
    series: [{
      type: 'bar',
      data: scores,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "18px",
          fontWeight: "normal",
          textOutline: "none"
        },
      },
      pointWidth: pointWidth,
      pointPadding: pointPadding
    }]
  }

  const options = merge({}, base, {
    plotOptions: {bar: {zones}}
  }, yAxis, xAxis, series, styles)

  const handlePrintLetters = async () => {
    setIsPrintModalOpen(false);

    // get parent letter data with subscores
    setPrintStatus(PRINT_STATUS.LOADING);
    const lettersData = await getParentLetterData(report, cutoffs);
    setParentLetterData(lettersData);
    setPrintStatus(PRINT_STATUS.READY);
  }

  return (
    <React.Fragment>
      <div className='dyslexiarisk-chart-wrapper'>
        <div className="dyslexiarisk-chart-row dyslexiarisk-chart-header">
          <div className="icon-buffer"/>
          <div className="dyslexiarisk-header-name"> Student </div>
          <div className="dyslexiarisk-header-locale"></div>
          <div className="dyslexiarisk-header-risklevel"> Risk Level<Tooltip type={tooltipTypes.riskLevel} /> </div>
          {isPrintButtonVisible && <PrintButton onClick={() => setIsPrintModalOpen(true)} />}
        </div>
        <div className="dyslexiarisk-chart-container" onScroll={onScroll}>
          <div className="dyslexiarisk-chart-row-container">
            {[...report.scores.values()].map(student => {
              const { names } = transform([student]);
              let name = names[0];
              return student.scores.map(({score, locale, labeledScore, status}, i) => {
                if(i > 0){ //only display student name on their first row
                  name = null;
                }
                let isPending = !!pendingStatuses[status];
                let stringScore = score ? score.toString() : null;
                return <RowDecorator key={i} name={name} score={stringScore} locale={locale} labeledScore={labeledScore} isPending={isPending}/>
              });
            })}
          </div>
          <div className="dyslexiarisk-highchart-container">
            <Chart options={options} callback={onChartLoad} />
          </div>
        </div>
        <div className="dyslexiarisk-chart-row dyslexiarisk-chart-footer"></div>
      </div>
      <PrintModal 
        isOpen={isPrintModalOpen}
        includeActivityLog={includeActivityLog}
        setIncludeActivityLog={setIncludeActivityLog}
        printLocale={printLocale}
        setPrintLocale={setPrintLocale}
        handleClose={() => setIsPrintModalOpen(false)}
        handlePrint={handlePrintLetters}
      />
      <ParentLetterPrintWrapper
        parentLetterData={parentLetterData}
        includeActivityLog={includeActivityLog}
        printLocale={printLocale}
      />
      <PrintStatusModal 
        printStatus={printStatus}
        setPrintStatus={setPrintStatus}
        onPrint={printParentLetters}
      />
    </React.Fragment>
  )
}

DyslexiaRisk.propTypes = {
  // describe + isRequired for validation
  // maybe AJV validators for more precision
  report: PropTypes.object,
}

export default DyslexiaRisk